import { useCallback, useEffect, useState } from "react";

const apiBaseUrl = process.env.REACT_APP_API_URL;

const ParticipantHistoryReport = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ currentPage: 1, lastPage: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("lastEventDate");
  const [direction, setDirection] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState({
    data: [],
    meta: null,
    loading: false,
  });

  const fetchRows = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page,
          perPage: 15,
          search,
          sort,
          direction,
        });
        const response = await fetch(
          `${apiBaseUrl}/api/analytics/participant-history?${params}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          },
        );
        const body = await response.json();
        setRows(body.data || []);
        setMeta(body.meta || { currentPage: 1, lastPage: 1, total: 0 });
      } finally {
        setLoading(false);
      }
    },
    [search, sort, direction],
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchRows(1), 250);
    return () => clearTimeout(timer);
  }, [fetchRows]);

  const changeSort = (nextSort) => {
    if (sort === nextSort) {
      setDirection(direction === "asc" ? "desc" : "asc");
      return;
    }
    setSort(nextSort);
    setDirection("desc");
  };

  const openDetails = async (row, page = 1) => {
    setSelected(row);
    setDetails((prev) => ({ ...prev, loading: true }));
    try {
      const params = new URLSearchParams({
        phone: row.phoneKey,
        page,
        perPage: 10,
      });
      const response = await fetch(
        `${apiBaseUrl}/api/analytics/participant-history/details?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        },
      );
      const body = await response.json();
      setDetails({ data: body.data || [], meta: body.meta, loading: false });
    } catch (_) {
      setDetails({ data: [], meta: null, loading: false });
    }
  };

  return (
    <div className="rounded-xl bg-white p-5 shadow-lg sm:p-6">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#004a7e]">
            Participant history
          </p>
          <h2 className="text-xl font-black text-gray-900">
            Participation by Phone Number
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Search unique participant phone numbers and review RSVP/ride history
            on demand.
          </p>
        </div>
        <input
          className="input input-bordered w-full lg:max-w-sm"
          placeholder="Search name, phone, or email..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="table table-zebra text-sm">
          <thead>
            <tr>
              <SortHeader
                label="Name(s)"
                sortKey="name"
                active={sort}
                direction={direction}
                onClick={changeSort}
              />
              <SortHeader
                label="Phone"
                sortKey="phone"
                active={sort}
                direction={direction}
                onClick={changeSort}
              />
              <SortHeader
                label="Events"
                sortKey="events"
                active={sort}
                direction={direction}
                onClick={changeSort}
              />
              <SortHeader
                label="Yes"
                sortKey="rideRequests"
                active={sort}
                direction={direction}
                onClick={changeSort}
              />
              <SortHeader
                label="No"
                sortKey="noResponses"
                active={sort}
                direction={direction}
                onClick={changeSort}
              />
              <SortHeader
                label="Pending"
                sortKey="pendingResponses"
                active={sort}
                direction={direction}
                onClick={changeSort}
              />
              <SortHeader
                label="Last event"
                sortKey="lastEventDate"
                active={sort}
                direction={direction}
                onClick={changeSort}
              />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center text-gray-500">
                  No participant history found.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.phoneKey}>
                  <td>
                    <span className="font-bold text-gray-900">
                      {row.names || "Unnamed"}
                    </span>
                    <div className="text-xs text-gray-500">{row.email}</div>
                  </td>
                  <td>{row.phone}</td>
                  <td>{row.eventCount}</td>
                  <td className="text-green-700">{row.rideRequests}</td>
                  <td className="text-red-600">{row.noResponses}</td>
                  <td className="text-orange-600">{row.pendingResponses}</td>
                  <td>{row.lastEventDate || "—"}</td>
                  <td>
                    <button
                      className="btn btn-xs bg-[#004a7e] text-white"
                      onClick={() => openDetails(row)}
                    >
                      History
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination meta={meta} disabled={loading} onPage={fetchRows} />
      {selected && (
        <HistoryDetails
          selected={selected}
          details={details}
          onClose={() => setSelected(null)}
          onPage={(page) => openDetails(selected, page)}
        />
      )}
    </div>
  );
};

const SortHeader = ({ label, sortKey, active, direction, onClick }) => (
  <th>
    <button
      type="button"
      className="font-black"
      onClick={() => onClick(sortKey)}
    >
      {label} {active === sortKey ? (direction === "asc" ? "↑" : "↓") : ""}
    </button>
  </th>
);

const Pagination = ({ meta, disabled, onPage }) => (
  <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
    <span>
      {meta.total || 0} records · page {meta.currentPage || 1} of{" "}
      {meta.lastPage || 1}
    </span>
    <div className="join">
      <button
        className="btn join-item btn-sm"
        disabled={disabled || meta.currentPage <= 1}
        onClick={() => onPage(meta.currentPage - 1)}
      >
        Prev
      </button>
      <button
        className="btn join-item btn-sm"
        disabled={disabled || meta.currentPage >= meta.lastPage}
        onClick={() => onPage(meta.currentPage + 1)}
      >
        Next
      </button>
    </div>
  </div>
);

const HistoryDetails = ({ selected, details, onClose, onPage }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-5 shadow-xl">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-black text-gray-900">
            {selected.names || selected.phone}
          </h3>
          <p className="text-sm text-gray-500">{selected.phone}</p>
        </div>
        <button className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
      {details.loading ? (
        <p className="text-gray-500">Loading history...</p>
      ) : (
        <div className="space-y-2">
          {details.data.map((item) => (
            <div
              key={item.id}
              className="rounded-lg border border-gray-100 p-3"
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-bold text-gray-900">{item.eventTitle}</p>
                <span className="text-xs text-gray-500">
                  {item.dateOfOccurrence}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {item.groupName || "No group"} · RSVP: {item.needRide || "—"}
                {item.assignedDriverName
                  ? ` · Driver: ${item.assignedDriverName}`
                  : ""}
              </p>
              {item.messageForAdmin && (
                <p className="mt-1 text-sm text-blue-600">
                  {item.messageForAdmin}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {details.meta && (
        <Pagination
          meta={details.meta}
          disabled={details.loading}
          onPage={onPage}
        />
      )}
    </div>
  </div>
);

export default ParticipantHistoryReport;
